import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppState } from 'store/configureStore';

interface WelcomeModalContextType {
  isWelcomeModalOpen: boolean;
  openWelcomeModal: () => void;
  closeWelcomeModal: () => void;
}

const WelcomeModalContext = createContext<WelcomeModalContextType | undefined>(undefined);

export const useWelcomeModal = () => {
  const context = useContext(WelcomeModalContext);
  if (!context) {
    throw new Error('useWelcomeModal must be used within a WelcomeModalProvider');
  }
  return context;
};

interface WelcomeModalProviderProps {
  children: ReactNode;
}

export const WelcomeModalProvider: React.FC<WelcomeModalProviderProps> = ({ children }) => {
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const {
    auth: { isLoggedIn },
    featureFlags: { ENABLE_SHOW_WELCOME_MODAL },
    wallet: { termsAccepted, autoCutoffMsRemaining },
  } = useAppState();
  useEffect(() => {
    if (termsAccepted) {
      // Check if modal has been dismissed before
      setIsWelcomeModalOpen(false);
    }
    if (isLoggedIn && ENABLE_SHOW_WELCOME_MODAL && !termsAccepted && autoCutoffMsRemaining) {
      // Show modal on first login only if feature flag is enabled
      setIsWelcomeModalOpen(true);
    }
  }, [isLoggedIn, ENABLE_SHOW_WELCOME_MODAL, termsAccepted, autoCutoffMsRemaining]);

  const openWelcomeModal = useCallback(() => {
    setIsWelcomeModalOpen(true);
  }, []);

  const closeWelcomeModal = useCallback(() => {
    setIsWelcomeModalOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      isWelcomeModalOpen,
      openWelcomeModal,
      closeWelcomeModal,
    }),
    [isWelcomeModalOpen, openWelcomeModal, closeWelcomeModal],
  );

  return <WelcomeModalContext.Provider value={value}>{children}</WelcomeModalContext.Provider>;
};
